import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { EndCustomer } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { endCustomerService } from "@/services/api/routeService";
import { Check, ChevronsUpDown, MapPin, Phone, User } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./button";
import { FormControl } from "./form";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Skeleton } from "./skeleton";

interface EndCustomerSelectProps {
  value?: number | number[];
  onChange: (value: number | number[]) => void;
  multiple?: boolean;
  placeholder?: string;
  className?: string;
  useFormControl?: boolean; // Nova prop para controlar se usa FormControl
  disabled?: boolean; // Add disabled prop
}

const EndCustomerSelect = ({
  value,
  onChange,
  multiple = false,
  placeholder = "Selecione cliente(s)",
  className,
  useFormControl = false, // Por padrão não usa FormControl
  disabled = false, // Add disabled prop with default value
}: EndCustomerSelectProps) => {
  const [customers, setCustomers] = useState<EndCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);
        const response = await endCustomerService.indexAll();
        const sortedCustomers = (response.data || []).sort((a, b) => {
          if (a.code && b.code) {
            return a.code.localeCompare(b.code);
          }
          if (a.code && !b.code) return -1;
          if (!a.code && b.code) return 1;
          return a.id - b.id;
        });
        setCustomers(sortedCustomers);
      } catch (err) {
        errorHandler(err, "Erro ao carregar clientes finais.");
      } finally {
        setLoading(false);
      }
    };
    loadCustomers();
  }, []);
  const handleSelect = (customerId: number) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(customerId)
        ? currentValues.filter((id) => id !== customerId)
        : [...currentValues, customerId];
      onChange(newValues);
    } else {
      onChange(customerId);
      setOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allCustomerIds = customers.map((customer) => customer.id);
      onChange(allCustomerIds);
    }
  };

  const handleDeselectAll = () => {
    if (multiple) {
      onChange([]);
    }
  };

  const getSelectedLabel = () => {
    if (multiple) {
      const selectedIds = Array.isArray(value) ? value : [];
      const selectedCustomers = customers.filter((customer) =>
        selectedIds.includes(customer.id),
      );

      return selectedCustomers.length > 0
        ? selectedCustomers.length === 1
          ? selectedCustomers[0].name
          : `${selectedCustomers.length} clientes selecionados`
        : placeholder;
    } else {
      if (!value || (Array.isArray(value) && value.length === 0))
        return placeholder;
      const selectedCustomer = customers.find(
        (customer) => customer.id === value,
      );
      return selectedCustomer ? selectedCustomer.name : placeholder;
    }
  };

  const getSearchValue = (customer: EndCustomer) => {
    const searchFields = [
      customer.name,
      customer.cnpj,
      customer.code,
      customer.address,
      customer.email,
      customer.phone_number,
      customer.area_code,
      customer.city,
      customer.state,
    ].filter(Boolean);

    return searchFields.join(" ").toLowerCase();
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full bg-muted" />
      </div>
    );
  }

  const ButtonComponent = (
    <Button
      variant="outline"
      role="combobox"
      aria-expanded={open}
      disabled={loading || disabled}
      className={cn(
        "justify-between w-full",
        (!value || (multiple && Array.isArray(value) && value.length === 0)) &&
          "text-muted-foreground",
        "dark:border-white",
        className,
      )}
    >
      {getSelectedLabel()}
      <ChevronsUpDown className="opacity-50" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {useFormControl ? (
          <FormControl>{ButtonComponent}</FormControl>
        ) : (
          ButtonComponent
        )}
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            const searchLower = search.toLowerCase();
            const valueLower = value.toLowerCase();

            if (valueLower.includes(searchLower)) return 1;
            return 0;
          }}
        >
          <CommandInput
            placeholder="Busque por nome, CNPJ, código, endereço..."
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>Nenhum cliente encontrado.</CommandEmpty>
            {multiple && (
              <CommandGroup>
                <div className="flex gap-2 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleSelectAll}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleDeselectAll}
                  >
                    Desmarcar todos
                  </Button>
                </div>
              </CommandGroup>
            )}

            <CommandGroup>
              {customers.map((customer) => (
                <CommandItem
                  value={getSearchValue(customer)}
                  key={customer.id}
                  onSelect={() => handleSelect(customer.id)}
                  className="flex flex-col items-start gap-1 p-3"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium">{customer.name}</span>
                    </div>
                    <Check
                      className={cn(
                        "ml-auto",
                        multiple
                          ? Array.isArray(value) && value.includes(customer.id)
                            ? "opacity-100"
                            : "opacity-0"
                          : customer.id === value
                            ? "opacity-100"
                            : "opacity-0",
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-1 text-xs text-gray-500 w-full">
                    {customer.phone_number && (
                      <div className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        <span>
                          {customer.area_code} {customer.phone_number}
                        </span>
                      </div>
                    )}

                    {customer.address && (
                      <div className="flex items-start gap-1">
                        <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
                        <span className="line-clamp-2">
                          {customer.address}
                          {customer.city &&
                            customer.state &&
                            `, ${customer.city} - ${customer.state}`}
                        </span>
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default EndCustomerSelect;
